import React from "react";
import DOMPurify from "dompurify";
import { Icon } from "../components/Icon";
import { LanguagePicker } from "../components/LanguagePicker";
import {
  CHECK_IN_BUTTON_LABEL,
  registrationClosedFollowUp,
  registrationClosedMessage
} from "../registrationClosedMessage";
import {
  normalizeShoutyCaps,
  formatMeetingWhen,
  hasVisibleContent
} from "../utils";
import { capacityChip, remainingLabel } from "../capacityChip";
import type { KioskLanguage, MeetingData } from "../types";

export interface WelcomeProps {
  data: MeetingData;
  openingContent?: string;
  onStart: () => void;
  onLookup: () => void;
  speakerSignupAvailable: boolean;
  languages: KioskLanguage[];
  languageCode: string;
  onSelectLanguage: (code: string) => void;
}

export const Welcome: React.FC<WelcomeProps> = ({
  data,
  onStart,
  onLookup,
  openingContent,
  speakerSignupAvailable,
  languages,
  languageCode,
  onSelectLanguage
}) => {
  const meetingTitle = normalizeShoutyCaps(data.meeting?.name ?? "Meeting");
  const meetingWhen = formatMeetingWhen(
    data.meeting?.eventStartUtc,
    data.meeting?.timeZoneId
  );
  const meetingWhere = data.meeting?.address || "Chambers";
  const boardName = normalizeShoutyCaps(data.project.name);

  const participationCheckInEnabled = data.participationCheckInEnabled === true;

  // The submit path rejects a new RSVP once the window closes or the meeting ends, so the
  // kiosk must not walk a resident all the way to Confirm just to fail there.
  const registrationClosed = data.meeting?.registrationOpen === false;
  const closedMessage =
    registrationClosedMessage(data.meeting?.registrationUnavailableReason) ??
    "Registration for this meeting is closed.";

  const closedBecauseFull =
    data.meeting?.registrationUnavailableReason === "AttendeeLimitReached";
  const attendeeCapacity =
    registrationClosed && !closedBecauseFull
      ? null
      : capacityChip(
          data.meeting?.registeredAttendeeLimit,
          data.meeting?.registeredAttendeeCount
        );

  // AB#9876 F14: the speaker chip mirrors the attendee chip's wording, thresholds and styling.
  // It stays visible when the list is full — that is the state it exists to report — and is
  // hidden only when speaker sign-up was never offered for this meeting.
  const speakerSignupOffered =
    data.meeting?.speakersCanRegister === true ||
    data.meeting?.speakerSignupUnavailableReason === "SpeakerLimitReached";
  const speakerCapacity =
    speakerSignupOffered && !registrationClosed
      ? capacityChip(
          data.meeting?.registeredSpeakerLimit,
          data.meeting?.registeredSpeakerCount
        )
      : null;

  return (
    <div className="welcome">
      <div className="welcome-scroll">
        <h1>Welcome to the {boardName} meeting</h1>
        {hasVisibleContent(openingContent) ? (
          <div
            className="kiosk-opening-content"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(openingContent as string)
            }}
          />
        ) : (
          <p>
            {speakerSignupAvailable
              ? "Sign in to register your attendance and add yourself to the speaker list for any items on today's agenda."
              : "Sign in to register your attendance for today's meeting."}
          </p>
        )}

        <LanguagePicker
          languages={languages}
          languageCode={languageCode}
          onSelectLanguage={onSelectLanguage}
        />

        {data.meeting && (
          <div className="meeting-card">
            <div className="when">{meetingWhen}</div>
            <div className="title">{meetingTitle}</div>
            <div className="where">{meetingWhere}</div>
          </div>
        )}

        {attendeeCapacity && (
          <div className={`capacity-chip ${attendeeCapacity.state}`}>
            {attendeeCapacity.state === "full"
              ? "In-person registration is full — please see the meeting clerk."
              : remainingLabel(attendeeCapacity.remaining, "in-person spot")}
          </div>
        )}

        {speakerCapacity && (
          <div className={`capacity-chip ${speakerCapacity.state}`}>
            {speakerCapacity.state === "full"
              ? "The speaker list is full — please see the meeting clerk."
              : remainingLabel(speakerCapacity.remaining, "speaker spot")}
          </div>
        )}
      </div>

      <div className="welcome-actions">
        {registrationClosed ? (
          <p className="capacity-full-cta" data-testid="registration-closed">
            {closedMessage}{" "}
            {registrationClosedFollowUp(
              data.meeting?.registrationUnavailableReason,
              participationCheckInEnabled
            )}
          </p>
        ) : (
          <button className="btn xl primary" type="button" onClick={onStart}>
            Start new registration <Icon name="arrow-right" size={24} />
          </button>
        )}
        {participationCheckInEnabled && (
          <button className="btn lg" type="button" onClick={onLookup}>
            {CHECK_IN_BUTTON_LABEL}
          </button>
        )}
      </div>
    </div>
  );
};
